import React, { useCallback, useMemo } from 'react';
import { orderedApplicationSteps, ApplicationAnalytics } from '@app/api';
import uniq from 'lodash/uniq';
import {
  LabelList,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

import { createUseStyles, useTheme } from 'react-jss';
import { Theme } from '@app/theme';
import { StatsCard } from './StatsCard';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    ...theme.fonts.label,
  },
  label: {
    ...theme.fonts.caption,
  },
}));

interface Props {
  analytics: ApplicationAnalytics[];
}
export const ApplicationCounts = ({ analytics }: Props) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const { t } = useTranslation();

  const [byLinks, setLinks] = React.useState(false);
  const toggleUseLinks = useCallback(() => setLinks((v) => !v), [setLinks]);

  const legend = useMemo(
    // source = "ref" cause a bug. So we add a space
    () =>
      uniq(
        analytics.map((a) =>
          byLinks
            ? `${a.sessionLinkId || 'Default'} `
            : `${a.source || 'Empty'} `,
        ),
      ),
    [analytics, byLinks],
  );
  const data = useMemo(() => {
    const byPage: Record<string, Record<string, number>> = {};
    for (const track of analytics) {
      if (track.fieldId) {
        continue;
      }
      const step = track.step;
      const source = track.source || 'Empty';
      const link = track.sessionLinkId || 'Default';

      if (!byPage[step]) {
        byPage[step] = {};
      }
      if (!byPage[step][source]) {
        byPage[step][source] = 0;
      }
      byPage[step][source] += track.count;

      if (!byPage[step][link]) {
        byPage[step][link] = 0;
      }
      byPage[step][link] += track.count;

      if (!byPage[step].total) {
        byPage[step].total = 0;
      }
      byPage[step].total += track.count;
    }
    return orderedApplicationSteps.map((step) => {
      return Object.entries(byPage[step] || {}).reduce(
        (acc, [source, count]) => {
          if (source === 'total') {
            acc.total = count;
          } else {
            acc[`${source} `] = count;
          }
          return acc;
        },
        { step } as any,
      );
    });
  }, [analytics]);

  const colors = useMemo(
    () => [
      theme.colors.blue,
      theme.colors.red,
      theme.colors.green,
      theme.colors.accentAccessible,
      theme.colors.yellowAccessible,
    ],
    [theme],
  );

  return (
    <StatsCard
      title={t('sessions.details.stats.applications')}
      toggleOnChange={toggleUseLinks}
      toggleChecked={byLinks}
      toggleLabel={t('sessions.details.stats.showLinks')}
    >
      <ResponsiveContainer height={300} className={classes.container}>
        <BarChart
          width={800}
          height={300}
          data={data}
          margin={{
            top: theme.marginBase * 3,
            right: theme.marginBase * 3,
            bottom: theme.marginBase * 3,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="step" tick={theme.fonts.caption} />
          <YAxis tick={theme.fonts.caption} />
          <Tooltip />
          {legend.length > 1 && <Legend />}
          {legend.map((source, index) => (
            <Bar
              dataKey={source}
              fill={colors[index % colors.length]}
              key={source}
              stackId="a"
            >
              {index === legend.length - 1 && (
                <LabelList
                  className={classes.label}
                  dataKey="total"
                  position="top"
                  fill={theme.colors.inkSecondary}
                />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </StatsCard>
  );
};
